import React, { useState, useEffect, useContext, useMemo } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import {
  getClient,
  updateClient,
  createClient,
  updateClientLogo,
  getClientPersonEmails,
  getVirtualProviders,
  getVirtualValuationProviders,
  getClientConfiguration,
  saveClientConfiguration,
  getAccounts,
  getAllReportCodes,
  getCancellationTypes,
} from '../../../services';
import { FormProvider, useForm, useWatch, useFormContext } from 'react-hook-form';
import { Button, Card, CardContent, CardHeader, CircularProgress, FormControlLabel, Grid, Typography } from '@material-ui/core';
import FormInput from '../../ReactHookForm/FormInput/FormInput';
import { FieldArray } from '../../ReactHookForm/Inputs';
import SectionHeader from '../../ReactHookForm/SectionHeader';
import { useHistory } from 'react-router';
import { toBase64, formatNumberWithCommas } from '../../../helpers';
import { AppContext } from '../../../AppContext';
import { Controller } from 'react-hook-form';
import ColoredCheckbox from '../../ColoredInputs/ColoredCheckbox';
import AccountRefForm from '../modals/AccountRefForm';
import FullScreenLoading from '../../FullScreenLoading';

const styles = theme => ({
  gridContainer: {
    // flex: 1,
    // alignSelf: 'stretch',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  existingLogo: {
    height: 150,
    width: '100%',
  },
  form: {
    width: '100%',
  },
  settingsCard: {
    height: '100%',
  },
  settingsCardHeader: {
    padding: '14px 18px',
    backgroundColor: '#f0f0f0',
    borderBottom: '1px solid #e0e0e0',
  },
  settingsCardHeaderTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: 'var(--jmi-blue)',
    letterSpacing: '0.01em',
  },
  settingsCardContent: {
    padding: '16px',
    '&:last-child': {
      paddingBottom: 16,
    },
  },
  cancellationFeeRow: {
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  cancellationFeeHeader: {
    fontWeight: 600,
    color: '#666',
    fontSize: '0.85rem',
    marginBottom: 8,
  },
  cancellationFeeLabel: {
    fontWeight: 500,
  },
  cancellationFeeDefault: {
    color: '#666',
  },
  accountSelectRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
  },
  accountSelectField: {
    flex: 1,
    minWidth: 0,
  },
  accountSelectAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 24,
    height: 31,
    cursor: 'pointer',
    color: 'green',
  },
  settingsCheckboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  clientDetailCheckbox: {
    margin: 0,
    marginLeft: 0,
    marginRight: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    '& .MuiCheckbox-root': {
      padding: 0,
      marginRight: 4,
    },
  },
});

const SettingsSectionCard = withStyles(styles)(({ classes, title, children }) => (
  <Card className={cn(classes.settingsCard, 'border-left-green')} elevation={2}>
    <CardHeader
      className={classes.settingsCardHeader}
      title={title}
      titleTypographyProps={{ className: classes.settingsCardHeaderTitle }}
    />
    <CardContent className={classes.settingsCardContent}>{children}</CardContent>
  </Card>
));

const ClientDetailCheckbox = ({ name, label, control, disabled, checked, onChange, classes }) => {
  const renderLabel = controlElement => (
    <FormControlLabel
      className={classes?.clientDetailCheckbox}
      control={controlElement}
      label={label}
    />
  );

  if (name) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) =>
          renderLabel(
            <ColoredCheckbox
              size="small"
              checked={field.value === true}
              onChange={e => field.onChange(e.target.checked === true)}
              name={name}
              disabled={disabled}
            />,
          )
        }
      />
    );
  }

  return renderLabel(
    <ColoredCheckbox size="small" checked={checked} onChange={onChange} disabled={disabled} />,
  );
};

const FORM_FIELDS = {
  LOGO_TEMP: 'logoTemp', // not a real input.  This holds HTML files
  LOGO_BASE64: 'logoBase64',
  CODE: 'code',
  NAME: 'name',
  ALIAS: 'alias',
  ACCOUNT_ID: 'accountId',

  BILLING_ADDRESS: 'billingAddress',
  PRIMARY_ADDRESS: 'primaryAddress',
  MAILING_ADDRESS: 'mailingAddress',
  ADDRESS_LINE_1: 'addressLine1',
  ADDRESS_LINE_2: 'addressLine2',
  ADDRESS_LINE_3: 'addressLine3',
  CITY: 'city',
  STATE: 'state',
  ZIP: 'zip',

  DAYS_FOR_QV_HO_COMPLETE: 'daysForQVHOComplete',
  CANCEL_QV_ON_HO_REJECT: 'cancelQVOnHOReject',
  QV_CLIENT_NAME_OVERRIDE: 'qvClientNameOverride',
  CONVERT_TO_VIRTUAL_ON_HO_REJECT: 'convertToVirtualOnHOReject',
  CONVERT_TO_REPORT: 'convertToReport',
  QV_DO_NOT_CONTACT_AGENT: 'qvDoNotContactAgent',
  PERPETUAL_HO_DUEDATE: 'perpetualHODuedate',
  REQUIRE_CALL_AHEAD: 'requireCallAhead',
  QA_REJECT_RESTRICTED: 'qaRejectRestricted',
  DISABLE_AGENT_NOTIFICATION: 'disableAgentNotification',
  SERVICE_ON_MISSING_CONTACT: 'serviceOnMissingContact',
  BILLING_CONTACT: 'billingContact',
  BILLING_INSTRUCTIONS: 'billingInstructions',
  CONVERSION_REPORT_CODE: 'conversionReportCode',
  DISABLE_INTERIOR_PHOTOS: 'disableInteriorPhotos',
  DATA_OFFLOAD_ON_COMPLETE: 'dataOffloadOnComplete',

  DELIVERY_METHOD: 'deliveryMethod',

  REPORT_PREFERENCE_EMAIL: 'email',
  REPORT_PREFERENCE_LINK: 'link',
  REPORT_PREFERENCE_FTP: 'ftp',
  REPORT_PREFERENCE_DOWNLOAD: 'download',
  REPORT_PREFERENCE_S3: 's3',
  REPORT_PREFERENCE_BRITECORE: 'briteCore',

  // email & link
  REPORT_PREFERENCE_EMAIL_UNDERWRITER: 'emailUnderwriter',
  REPORT_PREFERENCE_MULTIFILE: 'multifile',
  REPORT_PREFERENCE_PRIMARY_EMAIL: 'primaryEmail',
  REPORT_PREFERENCE_SECONDARY_EMAIL: 'secondaryEmail',

  // ftp
  REPORT_PREFERENCE_USERNAME: 'username',
  REPORT_PREFERENCE_PORT: 'port',
  REPORT_PREFERENCE_HOST: 'host',
  REPORT_PREFERENCE_PASSWORD: 'password',
  REPORT_PREFERENCE_DESTINATION_FOLDER_LOCATION: 'destFolderLocation',

  REPORT_PREFERENCE_BUCKET_NAME: 'bucketName',
  REPORT_PREFERENCE_REGION: 'region',
  // REPORT_PREFERENCE_USERNAME: "username", // already declared above. Leaving here for reference of required fields
  REPORT_PREFERENCE_ACCESS_KEY: 'accesskey',
  REPORT_PREFERENCE_SECURITY_KEY: 'securityKey',

  // BriteCore
  REPORT_PREFERENCE_AUTH_URL: 'authURL',
  REPORT_PREFERENCE_API_URL: 'apiurl',
  REPORT_PREFERENCE_CLIENT_ID: 'clientId',
  REPORT_PREFERENCE_CLIENT_SECRET: 'clientSecret',
  REPORT_PREFERENCE_NOTIFICATION_EMAIL: 'notificationEmail',
  REPORT_PREFERENCE_BRITECORE_EMAIL_UNDERWRITER: 'emailUnderwriter',

  IMPORT_VERIFICATION_EMAIL: 'orderUploadConfirmationEmails',

  VALUE: 'value',

  BETTERVIEW_CLIENT: 'betterviewClient',

  OPT_OUT_EMAIL: 'optOutEmail',
  CALLBACK_AUTH_KEY: 'callBackAuthKey',
  CAN_EMAIL_TO_SCHEDULE: 'canEmailToSchedule',
  OVERRIDE_BADGE_LOGO: 'overrideBadgeLogo',

  RECORD_TYPE: 'recordType',

  IMPORT_DISABLED: 'importDisabled',
  //Virtual Dashboard 
  VIRTUAL_DASHBOARD_PROVIDER_ID: 'virtualDashboardProviderId',
  VIRTUAL_VALUATION_PROVIDER_ID: 'virtualValuationProviderId',

  CANCELLATION_TYPE_FEE_OVERRIDE: 'cancellationTypeFeeOverride',
  CANCELLATION_TYPE_ID: 'cancellationTypeId',
  DEFAULT_FEE: 'defaultFee',
  FEE_OVERRIDE: 'feeOverride',
};

const normalizeCancellationType = type => ({
  cancellationTypeId: type.cancellationTypeId ?? type.CancellationTypeId,
  name: type.name ?? type.Name,
  defaultFee: type.defaultFee ?? type.DefaultFee,
});

const getClientCancellationFeeOverrides = clientConfig =>
  clientConfig?.[FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE] ??
  clientConfig?.CancellationTypeFeeOverride ??
  [];

const initializeCancellationFeeOverrides = (types, existingOverrides = []) =>
  types.map(type => {
    const existing = existingOverrides.find(
      override =>
        (override?.cancellationTypeId ?? override?.CancellationTypeId) === type.cancellationTypeId,
    );

    const feeOverride = existing?.feeOverride ?? existing?.FeeOverride;

    return {
      [FORM_FIELDS.CANCELLATION_TYPE_ID]: type.cancellationTypeId,
      [FORM_FIELDS.FEE_OVERRIDE]: feeOverride == null ? '' : feeOverride,
    };
  });

const buildCancellationTypeFeeOverridePayload = (types, overrides = []) =>
  types.map((type, index) => {
    const feeOverride = overrides[index]?.[FORM_FIELDS.FEE_OVERRIDE];
    const parsed =
      feeOverride === '' || feeOverride === undefined || feeOverride === null ? null : Number(feeOverride);

    return {
      [FORM_FIELDS.CANCELLATION_TYPE_ID]: type.cancellationTypeId,
      [FORM_FIELDS.FEE_OVERRIDE]: parsed == null || Number.isNaN(parsed) ? null : parsed,
    };
  });

const formValidationSchema = Yup.object().shape({
  [FORM_FIELDS.LOGO_BASE64]: Yup.string()
    .default('')
    .nullable(),
  [FORM_FIELDS.CODE]: Yup.string()
    .default('')
    .required('Required'),
  [FORM_FIELDS.NAME]: Yup.string()
    .default('')
    .required('Required'),
  [FORM_FIELDS.ALIAS]: Yup.string()
    .default('')
    .nullable(),
  [FORM_FIELDS.ACCOUNT_ID]: Yup.string()
    .default('')
    .required('Account is Required'),

  [FORM_FIELDS.RECORD_TYPE]: Yup.string()
    .default('')
    .nullable(),
  [FORM_FIELDS.IMPORT_DISABLED]: Yup.bool()
    .default(false)
    .nullable(),
  [FORM_FIELDS.VIRTUAL_DASHBOARD_PROVIDER_ID]: Yup.string().required('Required'),
  [FORM_FIELDS.VIRTUAL_VALUATION_PROVIDER_ID]: Yup.string().required('Required'),

  [FORM_FIELDS.PRIMARY_ADDRESS]: Yup.object().shape({
    [FORM_FIELDS.ADDRESS_LINE_1]: Yup.string()
      .required('Required')
      .default(''),
    // .nullable(),
    [FORM_FIELDS.ADDRESS_LINE_2]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.ADDRESS_LINE_3]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.CITY]: Yup.string()
      .required('Required')
      .default(''),
    [FORM_FIELDS.STATE]: Yup.string()
      .required('Required')
      .default(''),
    [FORM_FIELDS.ZIP]: Yup.string()
      .required('Required')
      .default(''),
  }),

  [FORM_FIELDS.BILLING_ADDRESS]: Yup.object().shape({
    [FORM_FIELDS.ADDRESS_LINE_1]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.ADDRESS_LINE_2]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.ADDRESS_LINE_3]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.CITY]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.STATE]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.ZIP]: Yup.string()
      .default('')
      .nullable(),
  }),

  [FORM_FIELDS.MAILING_ADDRESS]: Yup.object().shape({
    [FORM_FIELDS.ADDRESS_LINE_1]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.ADDRESS_LINE_2]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.ADDRESS_LINE_3]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.CITY]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.STATE]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.ZIP]: Yup.string()
      .default('')
      .nullable(),
  }),

  [FORM_FIELDS.BILLING_CONTACT]: Yup.string()
    .default('')
    .nullable(),
  [FORM_FIELDS.BILLING_INSTRUCTIONS]: Yup.string()
    .default('')
    .nullable(),
  [FORM_FIELDS.DAYS_FOR_QV_HO_COMPLETE]: Yup.number()
    .min(1)
    .max(30)
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr)),
  [FORM_FIELDS.IMPORT_VERIFICATION_EMAIL]: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string()
          .email('Must be a valid email')
          .default('')
          .required('Required'),
      }),
    )
    .default([])
    .nullable(),
  
  [FORM_FIELDS.CONVERSION_REPORT_CODE]: Yup.string()
    .default('')
    .nullable(),

  [FORM_FIELDS.DELIVERY_METHOD]: Yup.string()
    .nullable()
    .required('Required'),

  [FORM_FIELDS.REPORT_PREFERENCE_EMAIL]: Yup.object().shape({
    [FORM_FIELDS.REPORT_PREFERENCE_PRIMARY_EMAIL]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.SECONDARY_EMAIL]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_EMAIL_UNDERWRITER]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_MULTIFILE]: Yup.string()
      .default('')
      .nullable(),
  }),

  [FORM_FIELDS.REPORT_PREFERENCE_LINK]: Yup.object().shape({
    [FORM_FIELDS.REPORT_PREFERENCE_PRIMARY_EMAIL]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.SECONDARY_EMAIL]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_EMAIL_UNDERWRITER]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_MULTIFILE]: Yup.string()
      .default('')
      .nullable(),
  }),

  [FORM_FIELDS.REPORT_PREFERENCE_FTP]: Yup.object().shape({
    [FORM_FIELDS.REPORT_PREFERENCE_DESTINATION_FOLDER_LOCATION]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_HOST]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_PORT]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_USERNAME]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_PASSWORD]: Yup.string()
      .default('')
      .nullable(),
  }),

  [FORM_FIELDS.REPORT_PREFERENCE_S3]: Yup.object().shape({
    [FORM_FIELDS.REPORT_PREFERENCE_ACCESS_KEY]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_BUCKET_NAME]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_REGION]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_SECURITY_KEY]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_USERNAME]: Yup.string()
      .default('')
      .nullable(),
  }),

  [FORM_FIELDS.REPORT_PREFERENCE_BRITECORE]: Yup.object().shape({
    [FORM_FIELDS.REPORT_PREFERENCE_AUTH_URL]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_API_URL]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_CLIENT_ID]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_CLIENT_SECRET]: Yup.string()
      .default('')
      .nullable(),
    [FORM_FIELDS.REPORT_PREFERENCE_NOTIFICATION_EMAIL]: Yup.string()
      .default('')
      .nullable(),
  }),

  [FORM_FIELDS.OPT_OUT_EMAIL]: Yup.string().nullable(),

  [FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE]: Yup.array()
    .of(
      Yup.object().shape({
        [FORM_FIELDS.CANCELLATION_TYPE_ID]: Yup.number().nullable(),
        [FORM_FIELDS.FEE_OVERRIDE]: Yup.number()
          .nullable()
          .transform((_, orig) => {
            if (orig === '' || orig === null || orig === undefined) return null;
            const num = Number(orig);
            return Number.isNaN(num) ? null : num;
          })
          .min(0, 'Fee cannot be negative'),
      }),
    )
    .default([])
    .nullable(),
});

// window.yup = Yup;
// window.schema = formValidationSchema;

const DELIVERY_METHOD_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'ftp', label: 'FTP' },
  { value: 's3', label: 'S3' },
  { value: 'link', label: 'Link' },
  { value: 'download', label: 'Download' },
  { value: 'britecore', label: 'BriteCore' },
];

const buildPath = (...paths) => paths.join('.');

//const formRowNegativeMarginStyle = { marginTop: -25 };

const Hide = ({ hide, children }) => {
  return (
    <Grid item xs={12} container spacing={2} className={cn(hide && 'hide')}>
      {children}
    </Grid>
  );
};

// Stable component to prevent focus loss
const ImportVerificationEmailInput = ({ buildPath }) => {
  return (
    <Grid container>
      <Grid item xs={4} container justify="center" style={{ marginBottom: 5 }}>
        <FormInput type="text" name={buildPath(FORM_FIELDS.VALUE)} />
      </Grid>
      <Grid item xs={4}></Grid>
    </Grid>
  );
};

export const ClientDetailTab = withStyles(styles)(
  ({ classes, match, pageMode, clientId, useCardContainer, ...rest }) => {
    const { dispatch: appDispatch } = useContext(AppContext);
    const [isClientActive, setIsClientActive] = useState(false);
    const [disableAgentNotification, setDisableAgentNotification] = useState(false);
    const [serviceOnMissingContact, setServiceOnMissingContact] = useState(false);
    const [receiveImportVerification, setReceiveImportVerification] = useState(false);
    const [openOrderCount, setOpenOrderCount] = useState();
    const [showAddAccountRefModal, setShowAddAccountRefModal] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [virtualProvidersOptions, setVirtualProvidersOptions] = useState([]);
    const [virtualValuationProvidersOptions, setVirtualValuationProvidersOptions] = useState([]);
    const [reportCodes, setReportCodes] = useState([]);
    const [mailingAddress, setMailingAddress] = useState(true);
    const [billingAddress, setBillingAddress] = useState(true);
    const [cancellationTypes, setCancellationTypes] = useState([]);
    const [cancellationTypesLoading, setCancellationTypesLoading] = useState(true);
    const [isPageLoading, setIsPageLoading] = useState(true);


    const inCreateMode = pageMode === 'CREATE';

    const formMethods = useForm({
      resolver: yupResolver(formValidationSchema),
      reValidateMode: 'onSubmit',
      defaultValues: {
        [FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE]: [],
      },
    });


    const { setValue, getValues } = formMethods;

    const deliveryMethod = useWatch({
      control: formMethods.control,
      name: FORM_FIELDS.DELIVERY_METHOD,
    });

    const watchIsReportConversion = useWatch(
      {
        control: formMethods.control,
        name: FORM_FIELDS.CONVERT_TO_REPORT,
      }
    )

    const QVcancel = useWatch(
      {
        control: formMethods.control,
        name: FORM_FIELDS.CANCEL_QV_ON_HO_REJECT,
      }
    )

    const QVconvert = useWatch(
      {
        control: formMethods.control,
        name: FORM_FIELDS.CONVERT_TO_VIRTUAL_ON_HO_REJECT,
      }
    )
    const QVPerpetualHODuedate = useWatch(
      {
        control: formMethods.control,
        name: FORM_FIELDS.PERPETUAL_HO_DUEDATE,
      }
    )



    const history = useHistory();

    const onSubmitInvalid = async errors => {
      console.log('invalid', errors);
    };
    const onSubmitValid = async form => {
      const overrides = form[FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE] ?? [];
      const finalForm = {
        ...form,
        [FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE]: buildCancellationTypeFeeOverridePayload(
          cancellationTypes,
          overrides,
        ),
      };

      console.log('valid', finalForm);

      try {
        const response = await saveClientConfiguration(finalForm);
        console.log(response);
        history.goBack();
        // appDispatch({
        //   type: 'SET_SNACKBAR',
        //   snackbar: {
        //     isOpen: true,
        //     message: 'Client saved.',
        //     variant: 'success',
        //   },
        // });
      } catch (error) {
        console.log(error, error.message);
        // appDispatch({
        //   type: 'SET_SNACKBAR',
        //   snackbar: {
        //     isOpen: true,
        //     message: `${error.errorMessage ? error.errorMessage : error}`,
        //     variant: 'error',
        //   },
        // });
      }
    };

    const handleClientActiveState = ev => {
      setIsClientActive(!isClientActive);
      // if (isClientActive && openOrderCount > 0) {
      //   setIsClientActive(true);
      //   appDispatch({
      //     type: 'SET_SNACKBAR',
      //     snackbar: {
      //       isOpen: true,
      //       message: `Client cannot be deactivated because they have ${openOrderCount} open orders.`,
      //       variant: 'error',
      //     },
      //   });
      // }

      if (isClientActive) {
        setReceiveImportVerification(false);
      }
    };

    const getAccountOptions = async () => {
      const options = await getAccounts();
      setAccounts(
        options.map(option => {
          return { ...option, value: option.accountId, label: option.accountName };
        }),
      );
    };

    const getVirtualProvidersOptions = async () => {
      const options = await getVirtualProviders();
      const mapped = options.map(option => {
        return { ...option, value: option.virtualDashboardSourceLookupId, label: option.name };
      });
      setVirtualProvidersOptions(mapped);
      return mapped;
    };

    const getVirtualValuationProvidersOptions = async () => {
      const options = await getVirtualValuationProviders();
      const mapped = options.map(option => {
        return { ...option, value: option.virtualDashboardValuationLookupId, label: option.name };
      });
      setVirtualValuationProvidersOptions(mapped);
      return mapped;
    };

    const getReportCodes = async () => {
      const options = await getAllReportCodes();
      setReportCodes(
        options.map(option => {
          return { ...option, value: option.productCode, label: `${option.name} (${option.productCode})` };
        }),
      );
    };

    useMemo(() => {
      if (QVcancel) {
        formMethods.setValue(FORM_FIELDS.CONVERT_TO_VIRTUAL_ON_HO_REJECT, false)
        formMethods.setValue(FORM_FIELDS.PERPETUAL_HO_DUEDATE, false)
        formMethods.setValue(FORM_FIELDS.CONVERT_TO_REPORT, false)
      }

    }, [QVcancel])


    useMemo(() => {
      if (QVconvert) {
        formMethods.setValue(FORM_FIELDS.CANCEL_QV_ON_HO_REJECT, false)
        formMethods.setValue(FORM_FIELDS.PERPETUAL_HO_DUEDATE, false)
        formMethods.setValue(FORM_FIELDS.CONVERT_TO_REPORT, false)
      }

    }, [QVconvert])

    useMemo(() => {
      if (QVPerpetualHODuedate) {
        formMethods.setValue(FORM_FIELDS.CANCEL_QV_ON_HO_REJECT, false)
        formMethods.setValue(FORM_FIELDS.CONVERT_TO_VIRTUAL_ON_HO_REJECT, false)
        formMethods.setValue(FORM_FIELDS.CONVERT_TO_REPORT, false)
      }

    }, [QVPerpetualHODuedate])

    useMemo(() => {
      if (watchIsReportConversion) {
        formMethods.setValue(FORM_FIELDS.CANCEL_QV_ON_HO_REJECT, false)
        formMethods.setValue(FORM_FIELDS.CONVERT_TO_VIRTUAL_ON_HO_REJECT, false)
        formMethods.setValue(FORM_FIELDS.PERPETUAL_HO_DUEDATE, false)
      }

    }, [watchIsReportConversion])



    useEffect(() => {
      const getClientAsync = async (sourceOptions = [], valuationOptions = [], normalizedCancellationTypes = []) => {
        try {
          let clientConfig = await getClientConfiguration(clientId);
          let client = await getClient(clientId);
          
          setOpenOrderCount(client?.openOrderCount);
          setIsClientActive(client?.recordType === 'Active');
          setReceiveImportVerification(client?.importDisabled);
          
          
          // console.log(clientConfig[FORM_FIELDS.IMPORT_VERIFICATION_EMAIL]);
          // const formattedVerificationEmail = clientConfig[FORM_FIELDS.IMPORT_VERIFICATION_EMAIL].any
          //   ? clientConfig[FORM_FIELDS.IMPORT_VERIFICATION_EMAIL].split(';')?.map(value => ({ value }))
          //   : undefined;

          const formattedDeliveryMethod = !!clientConfig[FORM_FIELDS.DELIVERY_METHOD]
            ? clientConfig[FORM_FIELDS.DELIVERY_METHOD].toLowerCase()
            : undefined;

          const {
            [FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE]: _cancellationTypeFeeOverride,
            CancellationTypeFeeOverride: _cancellationTypeFeeOverridePascal,
            ...clientConfigWithoutCancellationFees
          } = clientConfig ?? {};

          const formData = {
            ...clientConfigWithoutCancellationFees,
            [FORM_FIELDS.RECORD_TYPE]: client.recordType,
            [FORM_FIELDS.IMPORT_DISABLED]: client.importDisabled,

            // [FORM_FIELDS.IMPORT_VERIFICATION_EMAIL]: formattedVerificationEmail,
            [FORM_FIELDS.DELIVERY_METHOD]: formattedDeliveryMethod,
          };

          // If client's provider is inactive (not in active-only dropdown), default to first active option
          const currentSourceId = formData[FORM_FIELDS.VIRTUAL_DASHBOARD_PROVIDER_ID];
          if (sourceOptions?.length && currentSourceId != null && currentSourceId !== '' && !sourceOptions.some(o => o.value == currentSourceId)) {
            formData[FORM_FIELDS.VIRTUAL_DASHBOARD_PROVIDER_ID] = sourceOptions[0].value;
          }
          const currentValuationId = formData[FORM_FIELDS.VIRTUAL_VALUATION_PROVIDER_ID];
          if (valuationOptions?.length && currentValuationId != null && currentValuationId !== '' && !valuationOptions.some(o => o.value == currentValuationId)) {
            formData[FORM_FIELDS.VIRTUAL_VALUATION_PROVIDER_ID] = valuationOptions[0].value;
          }

          formMethods.reset(formData);

          if (normalizedCancellationTypes.length) {
            formMethods.setValue(
              FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE,
              initializeCancellationFeeOverrides(
                normalizedCancellationTypes,
                getClientCancellationFeeOverrides(clientConfig),
              ),
            );
          }
        } catch (error) {
          console.log(error);
        }
      };
      getAccountOptions();
      getReportCodes();
      const loadOptionsAndClient = async () => {
        setCancellationTypesLoading(true);

        try {
          const [sourceOpts, valuationOpts, cancellationTypesData] = await Promise.all([
            getVirtualProvidersOptions(),
            getVirtualValuationProvidersOptions(),
            getCancellationTypes(),
          ]);

          const normalizedCancellationTypes = (cancellationTypesData ?? []).map(normalizeCancellationType);
          setCancellationTypes(normalizedCancellationTypes);

          if (!inCreateMode) {
            await getClientAsync(sourceOpts, valuationOpts, normalizedCancellationTypes);
          } else {
            formMethods.setValue(
              FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE,
              initializeCancellationFeeOverrides(normalizedCancellationTypes),
            );
          }
        } catch (error) {
          console.log(error);
        } finally {
          setCancellationTypesLoading(false);
          setIsPageLoading(false);
        }
      };
      loadOptionsAndClient();
    }, []);

    const handleAddAccountXRef = () => {
      setShowAddAccountRefModal(true);
    };

    const onSuccessCreateAccountRef = async response => {
      await getAccountOptions();
      formMethods.setValue(FORM_FIELDS.ACCOUNT_ID, response.accountId);
    };
    const onFailureCreateAccountRef = async error => {
      console.log('Error creating account:', error);
      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: error.message || 'Account name already exists.',
          variant: 'error',
        },
      });
    };

    const onAddressSameAsPrimary = isMailingAddress => {
      const lines = [
        FORM_FIELDS.ADDRESS_LINE_1,
        FORM_FIELDS.ADDRESS_LINE_2,
        FORM_FIELDS.ADDRESS_LINE_3,
        FORM_FIELDS.CITY,
        FORM_FIELDS.STATE,
        FORM_FIELDS.ZIP,
      ];

      lines.forEach(path => {
        let val = getValues(buildPath(FORM_FIELDS.PRIMARY_ADDRESS, path));
        if (isMailingAddress) {
          setValue(buildPath(FORM_FIELDS.MAILING_ADDRESS, path), val);
        } else {
          setValue(buildPath(FORM_FIELDS.BILLING_ADDRESS, path), val);
        }
      });
    };

    const handleQVChange = e => {

    };

    const areFieldsEqual = isMailingAddress => {
      let lines = [
        FORM_FIELDS.ADDRESS_LINE_1,
        FORM_FIELDS.ADDRESS_LINE_2,
        FORM_FIELDS.ADDRESS_LINE_3,
        FORM_FIELDS.CITY,
        FORM_FIELDS.STATE,
        FORM_FIELDS.ZIP,
      ];

      lines = lines.map(path => {
        if (isMailingAddress) {
          let mainValue = getValues(buildPath(FORM_FIELDS.PRIMARY_ADDRESS, path));
          let valueToMatch = getValues(buildPath(FORM_FIELDS.MAILING_ADDRESS, path));
          return valueToMatch === mainValue;
        } else {
          let mainValue = getValues(buildPath(FORM_FIELDS.PRIMARY_ADDRESS, path));
          let valueToMatch = getValues(buildPath(FORM_FIELDS.BILLING_ADDRESS, path));
          return valueToMatch === mainValue;
        }
      });

      return lines.every(x => x === true);
    };

    useEffect(() => {
      setMailingAddress(areFieldsEqual(true));
      setBillingAddress(areFieldsEqual(false));
    }, [getValues(buildPath(FORM_FIELDS.PRIMARY_ADDRESS, FORM_FIELDS.ADDRESS_LINE_1))]); // this works for the case but might not be the best way to handle

    return isPageLoading ? (
      <FullScreenLoading message="Preparing Client..." embedded titleVariant="h4" />
    ) : (
      <>
        {showAddAccountRefModal && (
          <AccountRefForm
            showModal={showAddAccountRefModal}
            setShowModal={setShowAddAccountRefModal}
            clientId={clientId}
            onSuccess={onSuccessCreateAccountRef}
            onFailure={onFailureCreateAccountRef}
          />
        )}
        <FormProvider {...formMethods} schema={formValidationSchema}>
          <form onSubmit={formMethods.handleSubmit(onSubmitValid, onSubmitInvalid)} className={classes.form}>
            <Grid
              //
              container
              direction="row"
              className={cn(classes.gridContainer)}
              spacing={3}
            >
              <Grid item xs={12} md={6}>
                <SettingsSectionCard title="Logo">
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormInput
                        type="file"
                        name={FORM_FIELDS.LOGO_TEMP}
                        label="Logo"
                        watches={[
                          //
                          FORM_FIELDS.LOGO_TEMP,
                          FORM_FIELDS.LOGO_BASE64,
                        ]}
                        setField={FORM_FIELDS.LOGO_BASE64}
                        computeSrc={async ([logoTemp, logoBase64]) => {
                          const file = logoTemp?.[0];

                          if (!file) {
                            return logoBase64;
                          }

                          const base64 = await toBase64(file);

                          return base64 || logoBase64;
                        }}
                        imageStyle={{
                          border: '1px solid black',
                          maxHeight: 250,
                          width: 'auto',
                        }}
                      />
                    </Grid>
                  </Grid>
                </SettingsSectionCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingsSectionCard title="Client">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormInput
                        type="text"
                        name={FORM_FIELDS.CODE}
                        label="Customer Code"
                        inputProps={{
                          readOnly: !inCreateMode,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormInput type="text" name={FORM_FIELDS.NAME} label="Customer Name" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormInput type="text" name={FORM_FIELDS.ALIAS} label="Alias" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <div className={classes.accountSelectRow}>
                        <div className={classes.accountSelectField}>
                          <FormInput
                            isFixed
                            type="select"
                            name={FORM_FIELDS.ACCOUNT_ID}
                            label="Select Account"
                            options={accounts}
                          />
                        </div>
                        <span
                          title="Add new account"
                          className={classes.accountSelectAction}
                          onClick={handleAddAccountXRef}
                        >
                          <AddCircleIcon fontSize="small" />
                        </span>
                      </div>
                    </Grid>
                  </Grid>
                </SettingsSectionCard>
              </Grid>

              <Grid item xs={12}>
                <SettingsSectionCard title="Address">
                  <Grid container spacing={2}>
                    <SectionHeader
                      title="Primary"
                      color="black"
                      fontSize={20}
                      containerStyle={{ marginBottom: -10 }}
                    />
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.PRIMARY_ADDRESS, FORM_FIELDS.ADDRESS_LINE_1)}
                  label="Address Line 1"
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.PRIMARY_ADDRESS, FORM_FIELDS.ADDRESS_LINE_2)}
                  label="Address Line 2"
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.PRIMARY_ADDRESS, FORM_FIELDS.ADDRESS_LINE_3)}
                  label="Address Line 3"
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput type="text" name={buildPath(FORM_FIELDS.PRIMARY_ADDRESS, FORM_FIELDS.CITY)} label="City" />
              </Grid>
              <Grid item xs={4}>
                <FormInput type="text" name={buildPath(FORM_FIELDS.PRIMARY_ADDRESS, FORM_FIELDS.STATE)} label="State" />
              </Grid>
              <Grid item xs={4}>
                <FormInput type="text" name={buildPath(FORM_FIELDS.PRIMARY_ADDRESS, FORM_FIELDS.ZIP)} label="Zip" />
              </Grid>

              {/* BILLING */}
              <SectionHeader
                title="Billing"
                color="black"
                fontSize={20}
                containerStyle={{ marginTop: 10, marginBottom: -20 }}
              />
              <Grid item xs={12}>
                <ClientDetailCheckbox
                  classes={classes}
                  label="Same as Primary Address"
                  checked={billingAddress}
                  onChange={e => {
                    onAddressSameAsPrimary(false);
                    setBillingAddress(e.target.checked);
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.BILLING_ADDRESS, FORM_FIELDS.ADDRESS_LINE_1)}
                  label="Address Line 1"
                  inputProps={{ readOnly: billingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.BILLING_ADDRESS, FORM_FIELDS.ADDRESS_LINE_2)}
                  label="Address Line 2"
                  inputProps={{ readOnly: billingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.BILLING_ADDRESS, FORM_FIELDS.ADDRESS_LINE_3)}
                  label="Address Line 3"
                  inputProps={{ readOnly: billingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.BILLING_ADDRESS, FORM_FIELDS.CITY)}
                  label="City"
                  inputProps={{ readOnly: billingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.BILLING_ADDRESS, FORM_FIELDS.STATE)}
                  label="State"
                  inputProps={{ readOnly: billingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.BILLING_ADDRESS, FORM_FIELDS.ZIP)}
                  label="Zip"
                  inputProps={{ readOnly: billingAddress }}
                />
              </Grid>

              {/* MAILING ADDRESS */}
              <SectionHeader
                title="Mailing"
                color="black"
                fontSize={20}
                containerStyle={{ marginTop: 10, marginBottom: -20 }}
              />
              <Grid item xs={12}>
                <ClientDetailCheckbox
                  classes={classes}
                  label="Same as Primary Address"
                  checked={mailingAddress}
                  onChange={e => {
                    onAddressSameAsPrimary(true);
                    setMailingAddress(e.target.checked);
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.MAILING_ADDRESS, FORM_FIELDS.ADDRESS_LINE_1)}
                  label="Address Line 1"
                  inputProps={{ readOnly: mailingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.MAILING_ADDRESS, FORM_FIELDS.ADDRESS_LINE_2)}
                  label="Address Line 2"
                  inputProps={{ readOnly: mailingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.MAILING_ADDRESS, FORM_FIELDS.ADDRESS_LINE_3)}
                  label="Address Line 3"
                  inputProps={{ readOnly: mailingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.MAILING_ADDRESS, FORM_FIELDS.CITY)}
                  label="City"
                  inputProps={{ readOnly: mailingAddress }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  type="text"
                  name={buildPath(FORM_FIELDS.MAILING_ADDRESS, FORM_FIELDS.STATE)}
                  label="State"
                  inputProps={{ readOnly: mailingAddress }}
                />
              </Grid>
                    <Grid item xs={4}>
                      <FormInput
                        type="text"
                        name={buildPath(FORM_FIELDS.MAILING_ADDRESS, FORM_FIELDS.ZIP)}
                        label="Zip"
                        inputProps={{ readOnly: mailingAddress }}
                      />
                    </Grid>
                  </Grid>
                </SettingsSectionCard>
              </Grid>

              <Grid item xs={12}>
                <SettingsSectionCard title="Billing Info">
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <FormInput type="text" name={FORM_FIELDS.BILLING_CONTACT} label="Contact Person" />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInput type="text" name={FORM_FIELDS.BILLING_INSTRUCTIONS} label="Instructions" />
                    </Grid>
                  </Grid>
                </SettingsSectionCard>
              </Grid>

              <Grid item xs={12}>
                <SettingsSectionCard title="Report Preferences">
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <FormInput
                        type="select"
                        name={FORM_FIELDS.DELIVERY_METHOD}
                        label="Delivery Method"
                        options={DELIVERY_METHOD_OPTIONS}
                        isAbsolute={true}
                      />
                    </Grid>
                    <Grid item xs={8}></Grid>

                    {/* JMI-1510 json data file delivered alongside the pdf. not supported for download or britecore */}
                    <Hide hide={!['email', 'link', 'ftp', 's3'].includes(deliveryMethod)}>
                      <Grid item xs={12}>
                        <ClientDetailCheckbox
                          classes={classes}
                          name={FORM_FIELDS.DATA_OFFLOAD_ON_COMPLETE}
                          label="Data Offload on Complete (deliver .json data file with report)"
                          control={formMethods.control}
                        />
                      </Grid>
                    </Hide>

                    {/* EMAIL */}
                    <Hide hide={deliveryMethod !== 'email'}>
                      <SectionHeader title="Email" />
                <SectionHeader
                  title="Completed Reports"
                  color="black"
                  fontSize={20}
                  containerStyle={{ marginBottom: -20 }}
                />

                <Grid item xs={4}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_EMAIL, FORM_FIELDS.REPORT_PREFERENCE_PRIMARY_EMAIL)}
                    label="Primary Email"
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_EMAIL, FORM_FIELDS.REPORT_PREFERENCE_SECONDARY_EMAIL)}
                    label="Secondary Email"
                  />
                </Grid>
                <Grid item xs={4}></Grid>

                <SectionHeader title="Options" color="black" fontSize={20} containerStyle={{ marginBottom: -10 }} />
                <Grid item xs={12}>
                  <div className={classes.settingsCheckboxGroup}>
                    <ClientDetailCheckbox
                      classes={classes}
                      name={buildPath(
                        FORM_FIELDS.REPORT_PREFERENCE_EMAIL,
                        FORM_FIELDS.REPORT_PREFERENCE_EMAIL_UNDERWRITER,
                      )}
                      label="Email Underwriter Completed Reports"
                      control={formMethods.control}
                    />
                    <ClientDetailCheckbox
                      classes={classes}
                      name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_EMAIL, FORM_FIELDS.REPORT_PREFERENCE_MULTIFILE)}
                      label="Multiple File Report Delivery"
                      control={formMethods.control}
                    />
                  </div>
                </Grid>

              </Hide>

              {/* FTP */}
              <Hide hide={deliveryMethod !== 'ftp'}>
                <SectionHeader title="FTP" />
                <SectionHeader
                  title="Completed Reports"
                  color="black"
                  fontSize={20}
                  containerStyle={{ marginBottom: -20 }}
                />

                <Grid item xs={4}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_FTP, FORM_FIELDS.REPORT_PREFERENCE_HOST)}
                    label="Host"
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_FTP, FORM_FIELDS.REPORT_PREFERENCE_PORT)}
                    label="Port"
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_FTP, FORM_FIELDS.REPORT_PREFERENCE_USERNAME)}
                    label="Username"
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_FTP, FORM_FIELDS.REPORT_PREFERENCE_PASSWORD)}
                    label="Password"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInput
                    type="text"
                    name={buildPath(
                      FORM_FIELDS.REPORT_PREFERENCE_FTP,
                      FORM_FIELDS.REPORT_PREFERENCE_DESTINATION_FOLDER_LOCATION,
                    )}
                    label="Destination Folder Location"
                  />
                </Grid>

              </Hide>

              {/* S3 */}
              <Hide hide={deliveryMethod !== 's3'}>
                <SectionHeader title="S3" />
                <SectionHeader
                  title="Completed Reports"
                  fcolor="black"
                  ontSize={20}
                  containerStyle={{ marginBottom: -20 }}
                />

                <Grid item xs={4}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_S3, FORM_FIELDS.REPORT_PREFERENCE_BUCKET_NAME)}
                    label="Bucket Name"
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_S3, FORM_FIELDS.REPORT_PREFERENCE_REGION)}
                    label="Region"
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_S3, FORM_FIELDS.REPORT_PREFERENCE_USERNAME)}
                    label="Username"
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_S3, FORM_FIELDS.REPORT_PREFERENCE_ACCESS_KEY)}
                    label="Access Key"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_S3, FORM_FIELDS.REPORT_PREFERENCE_SECURITY_KEY)}
                    label="Secret Key"
                  />
                </Grid>

              </Hide>

              {/* LINK */}
              <Hide hide={deliveryMethod !== 'link'}>
                <SectionHeader title="Link" />
                <SectionHeader
                  title="Completed Reports"
                  color="black"
                  fontSize={20}
                  containerStyle={{ marginBottom: -20 }}
                />

                <Grid item xs={4}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_LINK, FORM_FIELDS.REPORT_PREFERENCE_PRIMARY_EMAIL)}
                    label="Primary Email"
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_LINK, FORM_FIELDS.REPORT_PREFERENCE_SECONDARY_EMAIL)}
                    label="Secondary Email"
                  />
                </Grid>
                <Grid item xs={4}></Grid>

                <SectionHeader title="Options" color="black" fontSize={20} containerStyle={{ marginBottom: -10 }} />
                <Grid item xs={12}>
                  <div className={classes.settingsCheckboxGroup}>
                    <ClientDetailCheckbox
                      classes={classes}
                      name={buildPath(
                        FORM_FIELDS.REPORT_PREFERENCE_LINK,
                        FORM_FIELDS.REPORT_PREFERENCE_EMAIL_UNDERWRITER,
                      )}
                      label="Email Underwriter Completed Reports"
                      control={formMethods.control}
                    />
                    <ClientDetailCheckbox
                      classes={classes}
                      name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_LINK, FORM_FIELDS.REPORT_PREFERENCE_MULTIFILE)}
                      label="Multiple File Report Delivery"
                      control={formMethods.control}
                    />
                  </div>
                </Grid>

              </Hide>

              {/* DOWNLOAD */}
              <Hide hide={deliveryMethod !== 'download'}>
                <SectionHeader title="Download" />
                <SectionHeader
                  title="Completed Reports will be downloaded by the Client via the BlueSky portal : NO Options available"
                  color="black"
                  fontSize={20}
                />
              </Hide>

              {/* BriteCore */}
              <Hide hide={deliveryMethod !== 'britecore'}>
                <SectionHeader title="BriteCore" />
                <SectionHeader
                  title="Completed Reports"
                  color="black"
                  fontSize={20}
                  containerStyle={{ marginBottom: -20 }}
                />

                <Grid item xs={6}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_BRITECORE, FORM_FIELDS.REPORT_PREFERENCE_AUTH_URL)}
                    label="Authentication URL"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_BRITECORE, FORM_FIELDS.REPORT_PREFERENCE_API_URL)}
                    label="API URL"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    type="text"
                    name={buildPath(FORM_FIELDS.REPORT_PREFERENCE_BRITECORE, FORM_FIELDS.REPORT_PREFERENCE_CLIENT_ID)}
                    label="Client Id"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    type="text"
                    name={buildPath(
                      FORM_FIELDS.REPORT_PREFERENCE_BRITECORE,
                      FORM_FIELDS.REPORT_PREFERENCE_CLIENT_SECRET,
                    )}
                    label="Client Secret"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    type="text"
                    name={buildPath(
                      FORM_FIELDS.REPORT_PREFERENCE_BRITECORE,
                      FORM_FIELDS.REPORT_PREFERENCE_NOTIFICATION_EMAIL,
                    )}
                    label="Notification Email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <ClientDetailCheckbox
                    classes={classes}
                    name={buildPath(
                      FORM_FIELDS.REPORT_PREFERENCE_BRITECORE,
                      FORM_FIELDS.REPORT_PREFERENCE_BRITECORE_EMAIL_UNDERWRITER,
                    )}
                    label="Email Underwriter"
                    control={formMethods.control}
                  />
                </Grid>
              </Hide>
                  </Grid>
                </SettingsSectionCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <SettingsSectionCard title="Settings">
                          <div className={classes.settingsCheckboxGroup}>
                            <FieldArray
                              label="Import Verification Emails"
                              name={FORM_FIELDS.IMPORT_VERIFICATION_EMAIL}
                              control={formMethods.control}
                              Content={ImportVerificationEmailInput}
                            />
                            <Controller
                              name={FORM_FIELDS.RECORD_TYPE}
                              control={formMethods.control}
                              render={({ field }) => (
                                <FormControlLabel
                                  className={classes.clientDetailCheckbox}
                                  control={
                                    <ColoredCheckbox
                                      size="small"
                                      checked={field.value === 'Active'}
                                      disabled={openOrderCount > 0}
                                      onChange={e => {
                                        field.onChange(e.target.checked ? 'Active' : 'Inactive');

                                        if (!e.target.checked) {
                                          formMethods.setValue(FORM_FIELDS.IMPORT_DISABLED, true);
                                        }
                                      }}
                                      name={FORM_FIELDS.RECORD_TYPE}
                                      value={isClientActive === 'Active'}
                                    />
                                  }
                                  label="Active"
                                />
                              )}
                            />
                            <ClientDetailCheckbox
                              classes={classes}
                              name={FORM_FIELDS.IMPORT_DISABLED}
                              label="Import Disabled"
                              control={formMethods.control}
                              disabled={formMethods.getValues('recordType') !== 'Active'}
                            />
                            <ClientDetailCheckbox
                              classes={classes}
                              name={FORM_FIELDS.QA_REJECT_RESTRICTED}
                              label="Remove QA's ability to return orders (Admin roles remain unchanged) "
                              control={formMethods.control}
                            />
                            <ClientDetailCheckbox
                              classes={classes}
                              name={FORM_FIELDS.DISABLE_AGENT_NOTIFICATION}
                              label="Disable Agent Notification on import"
                              control={formMethods.control}
                            />
                            <ClientDetailCheckbox
                              classes={classes}
                              name={FORM_FIELDS.SERVICE_ON_MISSING_CONTACT}
                              label="Email service on missing contact info"
                              control={formMethods.control}
                            />
                          </div>
                        </SettingsSectionCard>
                      </Grid>

                      <Grid item xs={12}>
                        <SettingsSectionCard title="Virtual Dashboard Options">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <FormInput
                              isFixed
                              type="select"
                              name={FORM_FIELDS.VIRTUAL_DASHBOARD_PROVIDER_ID}
                              label="Select Virtual Dashboard Provider"
                              options={virtualProvidersOptions}
                            />
                            <FormInput
                              isFixed
                              type="select"
                              name={FORM_FIELDS.VIRTUAL_VALUATION_PROVIDER_ID}
                              label="Select Virtual Valuation Provider"
                              options={virtualValuationProvidersOptions}
                            />
                          </div>
                        </SettingsSectionCard>
                      </Grid>

                      <Grid item xs={12}>
                        <SettingsSectionCard title="Opt Out Notifications">
                          <FormInput
                            label="Email To Notify On Opt Out"
                            type="text"
                            name={buildPath(FORM_FIELDS.OPT_OUT_EMAIL)}
                          />
                        </SettingsSectionCard>
                      </Grid>
                    </Grid>
                  </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <SettingsSectionCard title="Schedule Via Email">
                          <ClientDetailCheckbox
                            classes={classes}
                            name={buildPath(FORM_FIELDS.CAN_EMAIL_TO_SCHEDULE)}
                            label="Allow inspector to email the insured"
                            control={formMethods.control}
                          />
                        </SettingsSectionCard>
                      </Grid>

                      <Grid item xs={12}>
                        <SettingsSectionCard title="Third Party Integrations">
                          <div className={classes.settingsCheckboxGroup}>
                            <ClientDetailCheckbox
                              classes={classes}
                              name={buildPath(FORM_FIELDS.BETTERVIEW_CLIENT)}
                              label="Betterview Button"
                              control={formMethods.control}
                            />
                            <FormInput
                              label="Status Callback Auth Key"
                              type="text"
                              name={buildPath(FORM_FIELDS.CALLBACK_AUTH_KEY)}
                            />
                            <ClientDetailCheckbox
                              classes={classes}
                              name={buildPath(FORM_FIELDS.OVERRIDE_BADGE_LOGO)}
                              label="Use client logo for field rep badge"
                              control={formMethods.control}
                            />
                          </div>
                        </SettingsSectionCard>
                      </Grid>

                      <Grid item xs={12}>
                        <SettingsSectionCard title="QuickVue Home Owner Settings">
                          <div className={classes.settingsCheckboxGroup}>
                            <ClientDetailCheckbox
                              classes={classes}
                              name={buildPath(FORM_FIELDS.QV_DO_NOT_CONTACT_AGENT)}
                              label="Do Not contact the Agent for missing contact details"
                              control={formMethods.control}
                            />
                            <FormInput
                              label="Client name override"
                              type="text"
                              name={buildPath(FORM_FIELDS.QV_CLIENT_NAME_OVERRIDE)}
                            />
                          </div>
                        </SettingsSectionCard>
                      </Grid>

                      <Grid item xs={12}>
                        <SettingsSectionCard title="Cancellation Fees">
                          {cancellationTypesLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                              <CircularProgress size={28} />
                            </div>
                          ) : cancellationTypes.length === 0 ? (
                            <Typography color="textSecondary">No cancellation types found.</Typography>
                          ) : (
                            <>
                              <Grid container spacing={2} className={classes.cancellationFeeHeader}>
                                <Grid item xs={5}>
                                  Cancellation Type
                                </Grid>
                                <Grid item xs={3}>
                                  Default Fee
                                </Grid>
                                <Grid item xs={4}>
                                  Override Fee
                                </Grid>
                              </Grid>

                              {cancellationTypes.map((type, index) => (
                                <Grid
                                  container
                                  spacing={2}
                                  key={type.cancellationTypeId}
                                  alignItems="center"
                                  className={classes.cancellationFeeRow}
                                >
                                  <Grid item xs={5}>
                                    <Typography className={classes.cancellationFeeLabel}>{type.name}</Typography>
                                  </Grid>
                                  <Grid item xs={3}>
                                    <Typography className={classes.cancellationFeeDefault}>
                                      ${formatNumberWithCommas(type.defaultFee, 2)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <FormInput
                                      type="number"
                                      name={`${FORM_FIELDS.CANCELLATION_TYPE_FEE_OVERRIDE}.${index}.${FORM_FIELDS.FEE_OVERRIDE}`}
                                      label="Override Fee"
                                      inputProps={{ min: 0, step: '0.01' }}
                                    />
                                  </Grid>
                                </Grid>
                              ))}
                            </>
                          )}
                        </SettingsSectionCard>
                      </Grid>
                    </Grid>
                  </Grid>

              {/* <Grid item xs={4}>
              <FieldArray
                label="Addresses"
                name={FORM_FIELDS.CLIENT_ADDRESS}
                useAccordion
                AccordionTitle={({ buildPath }) => {
                  // console.log('RERENDER: AccordionTitle');

                  const addressTypeValue = useWatch({
                    control: formMethods.control,
                    name: buildPath(FORM_FIELDS.ADDRESS_TYPE),
                  });
                  const label = ADDRESS_TYPE_OPTIONS.find(opt => opt.value === addressTypeValue)?.label ?? '-';
                  return `Type: ${label}`;
                }}
                AccordionContent={({ data, index, fieldArrayMethods, buildPath }) => {
                  // console.log('RERENDER: AccordionContent: ', FORM_FIELDS.CLIENT_ADDRESS);

                  return (
                    <Grid container>
                      <Grid item xs={12}>
                        <FormInput
                          type="select"
                          name={buildPath(FORM_FIELDS.ADDRESS_TYPE)}
                          label="Address Type"
                          options={ADDRESS_TYPE_OPTIONS}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput type="text" name={buildPath(FORM_FIELDS.AD1)} label="Address 1" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput type="text" name={buildPath(FORM_FIELDS.AD2)} label="Address 2" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput type="text" name={buildPath(FORM_FIELDS.AD3)} label="Address 3" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput type="text" name={buildPath(FORM_FIELDS.CITY)} label="City" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput type="text" name={buildPath(FORM_FIELDS.STATE)} label="State" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput type="text" name={buildPath(FORM_FIELDS.ZIP)} label="Zip" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInput type="text" name={buildPath(FORM_FIELDS.PLUS_4)} label="Extended Zip" />
                      </Grid>
                    </Grid>
                  );
                }}
              ></FieldArray>
            </Grid> */}

              <Grid item xs={12} container style={{ marginTop: 50 }}>
                <Button style={{ backgroundColor: '#315382', color: '#fff' }} variant="contained" type="submit">
                  {formMethods.formState.isSubmitting ? (
                    <>
                      <CircularProgress style={{ fill: '#fff', color: '#fff', marginRight: 10 }} size={25} />
                      Saving
                    </>
                  ) : (
                    'Save Client'
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </>
    );
  },
);

ClientDetailTab.propTypes = {
  pageMode: PropTypes.string,
  useCardContainer: PropTypes.bool,
};

ClientDetailTab.defaultProps = {
  pageMode: 'EDIT',
  useCardContainer: false,
};
